import sortBy from 'lodash.sortby';
import get from 'lodash.get';
import uniq from 'lodash.uniq';
import { SprayEventUuid } from 'services/sprayEvents/types';
import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import groupBy from 'lodash.groupby';
// Selectors
import { selectSprayEvents } from 'services/sprayEvents/selectors';
import { selectEntities } from 'services/entities/selectors';

import { makeSelectHarvestSchemaEntityTypeId } from 'services/schemas/selectors';
import { selectFormattedVarieties } from 'services/varieties/selectors';
import { getSprayEventVarietiesArray } from 'services/sprayEvents/utils';
import { selectSelectedIncompleteSprayEventsUuids } from '../../../SprayEventsVirtualizedTable/selectors';

import { IncompleteWeatherSprayEvent } from './types';

const selectWeatherIncompleteSprayEvents = createDeepEqualSelector(
    [
        selectSprayEvents,
        selectSelectedIncompleteSprayEventsUuids,
        selectEntities
    ],
    (sprayEvents, uuids: SprayEventUuid[], entities) => {
        const groupedUuids = uuids.reduce((result, uuid) => {
            const { startDate, entityId } = sprayEvents[uuid];
            const { name: entityName } = entities[entityId];
            const groupedEvents = { ...result };
            const key = `${startDate}_${entityId}`;

            if (!groupedEvents[key]) {
                groupedEvents[key] = {
                    key,
                    startDate,
                    entityName,
                    uuids: []
                };
            }

            groupedEvents[key].uuids.push(uuid);

            return groupedEvents;
        }, {} as Record<string, IncompleteWeatherSprayEvent>);

        const groupedUuidsArray = Object.values(groupedUuids);

        return sortBy(groupedUuidsArray, ['startDate', 'entityName']);
    }
);

const selectDisposalIncompleteSprayEvents = createDeepEqualSelector(
    [
        selectSprayEvents,
        selectSelectedIncompleteSprayEventsUuids,
        selectEntities,
        makeSelectHarvestSchemaEntityTypeId(),
        selectFormattedVarieties
    ],
    (
        sprayEvents,
        uuids: SprayEventUuid[],
        entities,
        harvestSchemaEntityTypeId,
        varieties
    ) =>
        sortBy(
            uuids.map((uuid) => {
                const { startDate, entityId, sprayEvent } = sprayEvents[uuid];
                const { sprayEntities } = sprayEvent;
                const sprayedEntities = sprayEntities
                    .map((entity) => get(entities, [entity.id, 'name']))
                    .join(', ');
                const { name: entityName } = entities[entityId];

                const sprayEventVarieties = uniq(
                    getSprayEventVarietiesArray(
                        sprayEntities,
                        harvestSchemaEntityTypeId,
                        entities
                    ).map((varietyId) =>
                        get(varieties, [varietyId, 'name'], null)
                    )
                ).join(', ');

                return {
                    uuid,
                    startDate,
                    entityName,
                    sprayedEntities,
                    sprayEventVarieties
                };
            }),
            ['startDate', 'entityName']
        )
);

const selectDisposalIncompleteSprayEventsGroupByStartDate =
    createDeepEqualSelector(
        selectDisposalIncompleteSprayEvents,
        (incompleteSprayEvents) =>
            groupBy(incompleteSprayEvents, 'startDate')
    );

const selectWeatherIncompleteSprayEventsGroupByStartDate =
    createDeepEqualSelector(
        selectWeatherIncompleteSprayEvents,
        (incompleteSprayEvents) => groupBy(incompleteSprayEvents, 'startDate')
    );

export {
    selectWeatherIncompleteSprayEvents,
    selectDisposalIncompleteSprayEvents,
    selectWeatherIncompleteSprayEventsGroupByStartDate,
    selectDisposalIncompleteSprayEventsGroupByStartDate
};
